import {
  BLANK_REGEX,
  CAPITAL_LETTERS_REGEX,
  LETTERS_REGEX,
  NUMBERS_REGEX,
  SNAKE_CASE_REGEX,
  SPACES_REGEX
} from './regex';

/**
 * @module String
 */

/**
 * Given a value, returns a boolean that indicates if it's a string.
 * @function
 * @example
 * // returns true
 * isString('Cuervo');
 * @example
 * // returns false
 * isString(10);
 * @param {*} value Value to be checked
 * @returns {boolean} Is a string?
 */
export const isString = value => typeof value === 'string';

/**
 * Converts a snake case to camel case.
 * @function
 * @example
 * // returns 'sanLorenzo'
 * snakeToCamelCase('san_lorenzo');
 * @param {string} string Snake case string
 * @returns {string} Camel case string
 */
export const snakeToCamelCase = string =>
  string.replace(SNAKE_CASE_REGEX, substring => substring?.[1].toUpperCase());

/**
 * Converts a camel case to snake case.
 * @function
 * @example
 * // returns 'san_lorenzo'
 * camelToSnakeCase('sanLorenzo');
 * @param {string} string Camel case string
 * @returns {string} Snake case string
 */
export const camelToSnakeCase = string =>
  string.replace(CAPITAL_LETTERS_REGEX, letter => `_${letter.toLowerCase()}`);

/**
 * Given a number n of characters, returns a function that takes the first N characters of a string.
 * @function
 * @example
 * // returns function
 * const take4Characters = takeNCharacters(4);
 * // returns 'stri'
 * take4Characters('string');
 * @param {number} n Number of characters
 * @returns {function} Function to be applied
 */
export const takeNCharacters = n => text => text.slice(0, n);

/**
 * Given a number n of characters, returns a function that drops the first N characters of a string.
 * @function
 * @example
 * // returns function
 * const drop4Characters = dropNCharacters(4);
 * // returns 'ng'
 * drop4Characters('string');
 * @param {number} n Number of characters
 * @returns {function} Function to be applied
 */
export const dropNCharacters = n => text => text.slice(n, text.length);

/**
 * Given n strings, concatenates them into a new string.
 * @function
 * @example
 * // returns 'San Lorenzo de Almagro'
 * concatenate('San', 'Lorenzo', 'de', 'Almagro');
 * @param  {...string} strings Strings to be concatenated
 * @returns {string} Concatenated string
 */
export const concatenate = (...strings) => strings.join(' ');

/**
 * Given a value, returns a boolean that indicates if it's a blank string.
 * @function
 * @example
 * // returns true
 * isBlank('');
 * @example
 * // returns false
 * isBlank('Cuervo');
 * @param {string} string Value to be checked
 * @returns {boolean} Is blank?
 */
export const isBlank = string => BLANK_REGEX.test(string);

/**
 * Removes all numbers of a string.
 * @function
 * @example
 * // returns 'string'
 * removeNumbers('1234s5t5r5i5n5g6789');
 * @param {string} string String
 * @returns {string} String without numbers
 */
export const removeNumbers = string => string.replace(NUMBERS_REGEX, '');

/**
 * Returns a string with only numbers, removing all other kind of characters.
 * @function
 * @example
 * // returns '1234555556789'
 * onlyNumbers('1234s5t5r5i5n5g6789');
 * @param {string} string String
 * @returns {string} String of numbers
 */
export const onlyNumbers = string => string.replace(LETTERS_REGEX, '');

/**
 * Replaces all spaces of a string with dashes.
 * @function
 * @example
 * // returns 'San-Lorenzo-de-Almagro'
 * replaceSpacesWithDashes('San Lorenzo de Almagro');
 * @param {string} string String
 * @returns {string} String with dashes instead of spaces
 */
export const replaceSpacesWithDashes = string => string.replace(SPACES_REGEX, '-');

/**
 * Converts first character of a string to uppercase letter and lowercases all other characters, if any.
 * @function
 * @example
 * // returns 'Cuervo'
 * capitalize('cUeRvO');
 * @param {string} string String to be capitalized
 * @returns {string} Capitalized string
 */
export const capitalize = string => string?.[0].toUpperCase().concat(string.slice(1).toLowerCase());

/**
 * Converts first character of a string to lowercase letter.
 * @function
 * @example
 * // returns 'cuervo'
 * decapitalize('Cuervo');
 * @param {string} string String to be decapitalized
 * @returns {string} Decapitalized string
 */
export const decapitalize = string => string?.[0].toLowerCase().concat(string.slice(1));

/**
 * Gets the first word from the given string.
 * @function
 * @example
 * // returns 'San'
 * getFirstWordFrom('San Lorenzo de Almagro');
 * @param {string} string String
 * @returns {string} First word of the given string
 */
export const getFirstWordFrom = string => string.split(' ').find(newString => newString !== '');

/**
 * Reverses the characters of a string.
 * @function
 * @example
 * // returns 'ovreuC'
 * reverse('Cuervo');
 * @param {string} string String
 * @returns {string} Reversed string
 */
export const reverse = string => string.split('').reverse().join('');

/**
 * Given a number n of characters, returns a function that repeats a string n times.
 * @function
 * @example
 * // returns function
 * const repeat3times = repeat(3);
 * // returns 'CuervoCuervoCuervo'
 * repeat3times('Cuervo');
 * @param {number} n Number
 * @returns {function} Function to be applied
 */
export const repeat = n => string => new Array(n + 1).join(string);

/**
 * Given a character and a string, takes the first n characters different to the given character of the string.
 * @function
 * @example
 * // returns '/recover'
 * takeSubstringUntil('?', '/recover?token=');
 * @param {string} character Character
 * @param {string} string String
 * @returns {string} The first n characters different to the given character of the string
 */
export const takeSubstringUntil = (character, string) => {
  const takeSubstring = takeNCharacters(string.indexOf(character));
  return takeSubstring(string);
};

/**
 * Given a string with hyphens, returns a camel cased string.
 * @function
 * @example
 * // returns 'sanLorenzo'
 * hyphensToCamelCase('san-lorenzo')
 * @param {string} str String with hyphens
 * @returns {string} Camel case string
 */
export const hyphensToCamelCase = str => {
  const arr = str.split(/[_-]/);
  let newStr = '';
  for (let i = 1; i < arr.length; i++) {
    newStr += arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
  }
  return arr[0] + newStr;
};

/**
 * Check if two given strings are equals
 * @param {string} strA String A
 * @param {string} srtB String B
 * @returns {bool} Are equals?
 */
export const stringsEquals = (strA, srtB) => strA.localeCompare(srtB) === 0;

export const alphabeticCompare = (aString, otherString) => aString.localeCompare(otherString) === 0;

// Browser-safe (no Node `Buffer`), UTF-8 aware
export const toBase64 = str => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

export const fromBase64 = str => {
  const binary = atob(str);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

export const capitalizeFirstLetter = string =>
  string?.[0].toUpperCase().concat(string.slice(1).toLowerCase());

export const formatStringIfExists = (string, formatFn) => (string ? formatFn(string) : '');

export const capitalizeEachFirstLetter = string => {
  const words = string.split(' ');
  return words.map(capitalizeFirstLetter).join(' ');
};
